import React, { useState, useEffect, useCallback, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useBrandAppConfig, retryMessage, getHeadersWithUniqueVisitorId } from "@brandclub/common-ui";
import { toast, Toaster } from "sonner";
import "./concierge-tailwind.css";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2";
import { ConciergeWidgetHeader } from "./ConciergeWidgetHeader";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useAutoSendFromUrl } from "./hooks/useAutoSendFromUrl";
import { useConversationLoader } from "./hooks/useConversationLoader";
import { useStreamingSearch } from "./hooks/useStreamingSearch";
import { useUrlSync } from "./hooks/useUrlSync";
import { useConciergeContext } from "./ConciergeContextProvider";
import { usePageContext } from "./hooks/usePageContext";
import { wrapSearchWithProductContext } from "./utils/wrapSearchWithProductContext";
import { useConciergeWidget } from "@/utils/hooks/useConciergeWidget";
import { useConciergePendingQuery } from "./stores/useConciergePendingQuery";

const queryClient = new QueryClient();

const ConciergeWidgetChatContent: React.FC = () => {
  // Brand config
  const { data: config } = useBrandAppConfig();
  const params = new URLSearchParams(window.location.search);
  const brandId = params.get("brandId") || null;
  const appConfigId = params.get("appConfigId") || null;

  // Input state
  const [searchInputValue, setSearchInputValue] = useState("");

  // Get conversation state from context
  const conversationState = useConciergeContext();

  // Get page context to include product info when on PDP
  const pageContext = usePageContext();

  // Get widget state and pending query
  const { isOpen } = useConciergeWidget();
  const { pendingQuery, pendingStacklineSku, clearPendingQuery } =
    useConciergePendingQuery();
  
  // Ref to track the last processed query to prevent duplicate sends
  // Store both query and stacklineSku to uniquely identify each query
  const lastProcessedQueryRef = useRef<{ query: string; stacklineSku: string | null } | null>(null);
  
  // Reset processed query when widget closes
  useEffect(() => {
    if (!isOpen) {
      lastProcessedQueryRef.current = null;
    }
  }, [isOpen]);

  // Auto-scroll behavior
  const {
    chatWrapperRef,
    messagesEndRef,
    shouldScrollOnNextMessageRef,
    scrollToBottom,
  } = useAutoScroll(
    conversationState.conversationHistory.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    conversationState.conversationHistory.length === 0
  );

  // Streaming search - expose performSearch for direct calls from pending query handler
  const { performSearch } = useStreamingSearch({
    brandId,
    appConfigId,
    conversationId: conversationState.conversationId,
    setConversationId: conversationState.setConversationId,
    setIsActivelyStreaming: conversationState.setIsActivelyStreaming,
    setStreamingAIResponse: conversationState.setStreamingAIResponse,
    setCurrentStatus: conversationState.setCurrentStatus,
    setConversationHistory: conversationState.setConversationHistory,
    setIsWaitingForFollowUpQueries:
      conversationState.setIsWaitingForFollowUpQueries,
    setStreamingProductGroups: conversationState.setStreamingProductGroups,
    setProductMetadataMap: conversationState.setProductMetadataMap,
    setPreviewSearchQueries: conversationState.setPreviewSearchQueries,
    setPreviewQueryProducts: conversationState.setPreviewQueryProducts,
    setFollowUpQuestions: conversationState.setFollowUpQuestions,
    setStreamingFeaturedProduct: conversationState.setStreamingFeaturedProduct,
    scrollToBottom,
    shouldScrollOnNextMessageRef,
  });

  // Handle search submission
  const handleSearch = useCallback(
    async (
      searchTerm: string,
      imageData?: string,
      imageMimeType?: string
    ) => {
      setSearchInputValue("");
      // Wrap search term with product context if on a PDP
      const searchTermWithContext = wrapSearchWithProductContext(
        searchTerm,
        pageContext
      );
      await performSearch(searchTermWithContext, imageData, imageMimeType);
    },
    [pageContext, performSearch]
  );

  // Handle retry message
  const handleRetry = async (messageIndex: number) => {
    if (!conversationState.conversationId) {
      toast.error("No conversation ID available");
      return;
    }

    try {
      // Remove messages from the message index onward
      conversationState.setConversationHistory((prev) =>
        prev.slice(0, messageIndex)
      );

      // Set streaming state
      conversationState.setIsActivelyStreaming(true);
      conversationState.setStreamingAIResponse("");
      conversationState.setCurrentStatus("");

      // Get headers
      const headers = await getHeadersWithUniqueVisitorId();

      // Call retry API and stream the response
      await retryMessage({
        conversationId: conversationState.conversationId,
        messageIndex,
        headersToInject: headers,
        onChunk: (chunk) => {
          conversationState.setStreamingAIResponse((prev) => prev + chunk);
        },
        onEvent: (event) => {
          if (event.type === "follow_up_queries") {
            conversationState.setFollowUpQuestions(event.data || []);
            conversationState.setIsWaitingForFollowUpQueries(false);
          }
        },
        onStatusChange: (status) => {
          conversationState.setCurrentStatus(status);
        },
        onComplete: () => {
          // Finalize the streaming message
          const streamedContent = conversationState.streamingAIResponse;
          if (streamedContent) {
            conversationState.setConversationHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: streamedContent,
                timestamp: new Date(),
              },
            ]);
          }

          // Reset streaming state
          conversationState.setStreamingAIResponse("");
          conversationState.setIsActivelyStreaming(false);
          conversationState.setCurrentStatus("");
        },
        onError: (error) => {
          console.error("Retry failed:", error);
          toast.error("Failed to retry message");
          conversationState.setIsActivelyStreaming(false);
          conversationState.setStreamingAIResponse("");
        },
        onConversationId: (id) => {
          // Conversation ID should already be set, but update if needed
          if (id !== conversationState.conversationId) {
            conversationState.setConversationId(id);
          }
        },
        onNewProductMetadata: (products) => {
          // Add products to the map
          const newMap = new Map(conversationState.productMetadataMap);
          products.forEach((product) => {
            newMap.set(product.stacklineSku, product);
          });
          conversationState.setProductMetadataMap(newMap);
        },
        onFeaturedProduct: (product, question) => {
          conversationState.setStreamingFeaturedProduct({ product, question });
        },
      });
    } catch (error) {
      console.error("Error retrying message:", error);
      toast.error("Failed to retry message");
      conversationState.setIsActivelyStreaming(false);
    }
  };

  // Handle update feedback
  const handleUpdateFeedback = (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => {
    // Update the feedback in the conversation history
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || undefined,
          };
        }
        return msg;
      });
    });
  };

  // Conversation loader
  const { isLoadingConversationQuery } = useConversationLoader({
    conversationId: conversationState.conversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: conversationState.conversationHistory.length,
    onConversationLoaded: (
      messages,
      productMap,
      followUpQuestions,
      previewSearchQueries,
      previewQueryProducts
    ) => {
      conversationState.setProductMetadataMap(productMap);
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      conversationState.setPreviewSearchQueries(previewSearchQueries);
      conversationState.setPreviewQueryProducts(previewQueryProducts);
    },
    onConversationNotFound: () => {
      conversationState.setConversationId(null);
    },
  });

  // Sync conversationId with URL (disabled in widget)
  // useUrlSync(conversationState.conversationId);

  // Auto-send from URL params (disabled in widget)
  // useAutoSendFromUrl({
  //   isActivelyStreaming: conversationState.isActivelyStreaming,
  //   conversationLength: conversationState.conversationHistory.length,
  //   onSearch: handleSearch,
  // });

  // Handle pending queries from PDP conversation starters
  useEffect(() => {
    // Only process if widget is open and there's a pending query
    // Check if we've already processed this exact query to prevent duplicates
    // Note: We allow sending even if actively streaming to support multiple queries
    if (isOpen && pendingQuery) {
      // Check if this is the same query we just processed
      const isDuplicate =
        lastProcessedQueryRef.current?.query === pendingQuery &&
        lastProcessedQueryRef.current?.stacklineSku === pendingStacklineSku;

      if (isDuplicate) {
        // This is a duplicate, skip it
        return;
      }

      // Mark this query as processed before clearing
      lastProcessedQueryRef.current = {
        query: pendingQuery,
        stacklineSku: pendingStacklineSku,
      };

      // Store the values before clearing to avoid race conditions
      const queryToSend = pendingQuery;
      const stacklineSkuToUse = pendingStacklineSku;

      // Clear the pending query immediately to prevent duplicate sends
      clearPendingQuery();

      // Wrap query with product context if needed
      let messageToSend = queryToSend;
      if (stacklineSkuToUse) {
        const productPageContext = {
          pageType: "pdp" as const,
          stacklineSku: stacklineSkuToUse,
        };
        messageToSend = wrapSearchWithProductContext(
          queryToSend,
          productPageContext
        );
      }

      // Extract display content for user message (after wrapping, matching performSearch logic)
      const featuredProductRegex =
        /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
      const featuredMatch = messageToSend.match(featuredProductRegex);
      const displayContent =
        featuredMatch && featuredMatch[2] ? featuredMatch[2] : queryToSend;

      // Add user message to conversation history IMMEDIATELY before calling performSearch
      // This ensures it's added even when already streaming (which might cause issues in performSearch)
      const userMessage = {
        role: "user" as const,
        content: displayContent,
        timestamp: new Date(),
      };
      conversationState.setConversationHistory((prev) => [...prev, userMessage]);

      // Call performSearch with skipAddingUserMessage=true since we've already added it
      performSearch(messageToSend, undefined, undefined, true);
    }
  }, [
    isOpen,
    pendingQuery,
    pendingStacklineSku,
    performSearch,
    clearPendingQuery,
    conversationState.setConversationHistory,
  ]);

  return (
    <>
      <Toaster />
      <div
        className="concierge-root"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <ConciergeWidgetHeader />
        <ConciergeChatAreaV2
          appName={config?.appName}
          brandId={brandId}
          conversationId={conversationState.conversationId}
          isEmbedded={true}
          conversationHistory={conversationState.conversationHistory}
          productMetadataMap={conversationState.productMetadataMap}
          streamingProductGroups={conversationState.streamingProductGroups}
          previewSearchQueries={conversationState.previewSearchQueries}
          previewQueryProducts={conversationState.previewQueryProducts}
          followUpQuestions={conversationState.followUpQuestions}
          streamingFeaturedProduct={conversationState.streamingFeaturedProduct}
          isActivelyStreaming={conversationState.isActivelyStreaming}
          streamingAIResponse={conversationState.streamingAIResponse}
          currentStatus={conversationState.currentStatus}
          isWaitingForFollowUpQueries={
            conversationState.isWaitingForFollowUpQueries
          }
          isLoadingConversationQuery={isLoadingConversationQuery}
          handleSearch={handleSearch}
          onRetry={handleRetry}
          onUpdateFeedback={handleUpdateFeedback}
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
        />
      </div>
    </>
  );
};

export const ConciergeWidgetChat: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConciergeWidgetChatContent />
    </QueryClientProvider>
  );
};
